/**
 * iter153 - Action 10. GDPR Article 9 explicit consent for special-category
 * (health/condition) data. Shown ONLY to EU users during signup, immediately
 * after the standard account-creation step.
 *
 * On accept  -> POST /api/gdpr/consent (records timestamp + IP + user_agent).
 * On decline -> account still gets created but `consented_conditions=false`
 *               so the child-profile form hides the condition selector.
 */
import { useState } from "react";
import api from "@/lib/api";
import { ShieldCheck } from "lucide-react";

export default function ConditionDataConsent({ onAccept, onDecline }) {
  const [consented, setConsented] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const submit = async (accepted) => {
    setSubmitting(true);
    try {
      await api.post("/gdpr/consent", {
        consent_type: "article_9_condition_data",
        consented:    accepted,
      });
    } catch (_) { /* recorded locally too; harmless if backend hiccups */ }
    setSubmitting(false);
    (accepted ? onAccept : onDecline)?.();
  };

  return (
    <div
      data-testid="gdpr-condition-consent"
      className="max-w-xl mx-auto p-6 rounded-2xl bg-white border border-[#CBD5E0]"
    >
      <div className="flex items-center gap-2 mb-3">
        <ShieldCheck className="h-6 w-6 text-[#0D3D6B]" strokeWidth={2.5} />
        <h2 className="font-display text-xl font-bold text-[#0D3D6B]">
          Permission to process health and disability information
        </h2>
      </div>
      <p className="text-[15px] text-[#4A5568] leading-relaxed mb-4">
        SpectraLearn generates personalised educational resources based on a
        child's learning condition (such as autism, ADHD, dyslexia, Down syndrome,
        or learning disabilities). This information is classified as special
        category health data under the EU General Data Protection Regulation
        (GDPR Article 9).
      </p>
      <p className="text-[15px] text-[#4A5568] leading-relaxed mb-5">
        We need your explicit consent to store and process this information.
        You can withdraw consent at any time from your Account Settings.
        Without this consent, you can still use SpectraLearn but condition-specific
        personalisation will not be available.
      </p>
      <label className="flex items-start gap-3 mb-6 cursor-pointer">
        <input
          type="checkbox"
          checked={consented}
          onChange={(e) => setConsented(e.target.checked)}
          data-testid="gdpr-consent-checkbox"
          className="mt-1 h-[18px] w-[18px] shrink-0 accent-[#0D3D6B]"
        />
        <span className="text-[15px] text-[#1B2A3B] leading-relaxed">
          <strong>I explicitly consent</strong> to SpectraLearn storing and
          processing health and disability information about the child I am
          supporting, for the purpose of generating personalised educational
          resources. I understand I can withdraw this consent at any time.
        </span>
      </label>
      <div className="flex flex-col sm:flex-row gap-3">
        <button
          onClick={() => submit(true)}
          disabled={!consented || submitting}
          data-testid="gdpr-consent-accept-btn"
          className={`flex-1 h-11 rounded-xl font-semibold text-[15px] ${
            consented ? "bg-[#0D3D6B] text-white hover:bg-[#0a2f54]" : "bg-[#CBD5E0] text-white cursor-not-allowed"
          }`}
        >
          I consent
        </button>
        <button
          onClick={() => submit(false)}
          disabled={submitting}
          data-testid="gdpr-consent-decline-btn"
          className="flex-1 h-11 rounded-xl border border-[#CBD5E0] bg-white text-[15px] text-[#4A5568] hover:bg-[#F7FAFC]"
        >
          No thanks, continue without condition personalisation
        </button>
      </div>
    </div>
  );
}
